import { useEffect, useMemo, useState, type SyntheticEvent } from "react";

import type { Dashboard } from "../application/ports";
import type { DependentProfileView, EventView, GiftListView, NotificationPreferenceView, NotificationView, PassportVisibility, TrustedPassportAccessView } from "../domain/cadohm";
import { ApiClientError, CadohmApiClient } from "../infrastructure/api-client";

type Phase = "auth" | "verify" | "recovery-request" | "recovery-complete" | "onboarding" | "dashboard";

const messages: Record<string, string> = {
  ACCOUNT_ALREADY_EXISTS: "Un compte existe déjà pour cette adresse.",
  ACCOUNT_DELETE_CONFIRMATION_REQUIRED: "Recopiez SUPPRIMER pour confirmer.",
  CREDENTIALS_INVALID: "Adresse ou mot de passe incorrect.",
  DEPENDENT_CO_GUARDIAN_REVOKE_DENIED: "Seul le responsable principal peut retirer ce coresponsable.",
  DEPENDENT_INVITATION_INVALID: "Cette invitation est invalide, expirée ou destinée à un autre compte.",
  DEPENDENT_PROFILE_ACCESS_DENIED: "Vous n'avez plus accès à ce profil.",
  DEPENDENT_PROFILE_ADMIN_REQUIRED: "Seul le responsable principal peut envoyer cette invitation.",
  DEPENDENT_PROFILE_NAME_INVALID: "Le nom du profil doit contenir entre 2 et 80 caractères.",
  DEPENDENT_SELF_INVITATION_FORBIDDEN: "Vous gérez déjà ce profil.",
  EMAIL_INVALID: "Saisissez une adresse e-mail valide.",
  EVENT_BENEFICIARY_INVALID: "Le nom du bénéficiaire doit contenir entre 2 et 80 caractères.",
  EVENT_BENEFICIARY_INVITATION_DENIED: "Le bilan ne peut être proposé qu'au bénéficiaire d'un événement clôturé, distinct des membres actifs.",
  EVENT_BENEFICIARY_OUTCOME_INVALID: "Choisissez un état post-événement proposé.",
  EVENT_BENEFICIARY_OUTCOME_UPDATE_DENIED: "Seul le bénéficiaire lié peut mettre à jour cet état après la clôture.",
  EVENT_BUYER_ASSIGN_DENIED: "Seul l'organisateur peut désigner comme acheteur un participant actif de cet événement ouvert.",
  EVENT_BUYER_REVOKE_DENIED: "Ce participant n'est plus l'acheteur désigné ou vous n'êtes pas l'organisateur.",
  EVENT_COORGANIZER_ASSIGN_DENIED: "Seul l'organisateur peut désigner comme coorganisateur un participant actif de cet événement ouvert.",
  EVENT_COORGANIZER_REVOKE_DENIED: "Ce participant n'est plus coorganisateur ou vous n'êtes pas l'organisateur.",
  EVENT_CLOSE_DENIED: "Seul l'organisateur peut clôturer un événement ouvert après une décision finale ou un tirage actif.",
  EVENT_DATE_IN_PAST: "Choisissez une date qui n'est pas passée.",
  EVENT_DATE_INVALID: "Choisissez une date valide.",
  EVENT_DECISION_DEADLINE_AFTER_EVENT: "Choisissez une échéance de décision au plus tard le jour de l'événement.",
  EVENT_INVITATION_DENIED: "Seuls l'organisateur ou le coorganisateur peuvent inviter dans un événement ouvert.",
  EVENT_INVITATION_INVALID: "Cette invitation est invalide, expirée ou destinée à un autre compte.",
  EVENT_ACCESS_DENIED: "Vous ne participez pas à cet événement.",
  EVENT_GIFT_IDEA_INVALID: "L'idée doit contenir entre 2 et 140 caractères.",
  EVENT_GIFT_COMMENT_DENIED: "Seuls les membres actifs peuvent commenter cette idée.",
  EVENT_GIFT_COMMENT_INVALID: "Le commentaire doit contenir entre 2 et 600 caractères.",
  EVENT_GIFT_DECISION_DENIED: "Seuls l'organisateur ou l'acheteur désigné peuvent choisir une idée de cet événement ouvert.",
  EVENT_GIFT_DECISION_EXPLANATION_INVALID: "Expliquez la décision en 2 à 600 caractères.",
  EVENT_GIFT_VOTE_DENIED: "Seuls les membres actifs peuvent modifier leur vote sur cette idée.",
  EVENT_GIFT_RESERVATION_CANCEL_DENIED: "Seule la personne ayant réservé peut libérer ce cadeau.",
  EVENT_GIFT_RESERVATION_UNAVAILABLE: "Ce cadeau vient d'être réservé ou n'est plus disponible.",
  EVENT_EXTERNAL_POT_UPDATE_DENIED: "Seul l'organisateur peut modifier la cagnotte externe.",
  EXTERNAL_POT_PROVIDER_INVALID: "Indiquez le nom du service de cagnotte.",
  EXTERNAL_POT_URL_INVALID: "Utilisez un lien HTTPS valide sans identifiant intégré.",
  EVENT_OPEN_DENIED: "Seul l'organisateur peut ouvrir ce brouillon.",
  EVENT_PARTICIPANT_REVOKE_DENIED: "Seuls l'organisateur ou le coorganisateur peuvent retirer un autre participant actif.",
  PASSPORT_EVENT_SHARE_DENIED: "Choisissez la visibilité « participants d'un événement » et rejoignez un événement ouvert.",
  PASSPORT_EVENT_SHARE_NOT_FOUND: "Ce passeport n'était plus partagé avec cet événement.",
  PASSPORT_NOT_FOUND: "Créez d'abord votre passeport cadeau.",
  PASSPORT_PREFERENCE_INVALID: "Chaque préférence doit contenir au maximum 600 caractères.",
  PASSPORT_TRUSTED_INVITATION_DENIED: "Passez le passeport en visibilité « proches autorisés » avant d'inviter.",
  PASSPORT_TRUSTED_INVITATION_INVALID: "Cette invitation passeport est invalide, expirée ou destinée à un autre compte.",
  PASSPORT_TRUSTED_SELF_INVITATION_FORBIDDEN: "Vous ne pouvez pas vous inviter vous-même.",
  PASSPORT_TRUSTED_VIEWER_REVOKE_DENIED: "Ce proche n'avait plus accès à votre passeport.",
  PASSPORT_VISIBILITY_INVALID: "Choisissez une visibilité valide.",
  EVENT_SELF_INVITATION_FORBIDDEN: "Vous participez déjà comme organisateur.",
  EVENT_TITLE_INVALID: "Le titre doit contenir entre 2 et 100 caractères.",
  GIFT_LIST_ACCESS_DENIED: "Vous n'avez plus accès à cette liste.",
  GIFT_LIST_EDITOR_REVOKE_DENIED: "Seul le propriétaire peut retirer ce proche.",
  GIFT_LIST_INVITATION_INVALID: "Cette invitation est invalide, expirée ou destinée à un autre compte.",
  GIFT_LIST_ITEM_INVALID: "Le cadeau doit contenir entre 2 et 140 caractères.",
  GIFT_LIST_OWNER_REQUIRED: "Seul le propriétaire peut inviter un proche.",
  GIFT_LIST_SELF_INVITATION_FORBIDDEN: "Vous ne pouvez pas vous inviter vous-même.",
  GIFT_LIST_TITLE_INVALID: "Le nom de la liste doit contenir entre 2 et 100 caractères.",
  MERCHANT_NAME_INVALID: "Indiquez le nom du marchand.",
  MERCHANT_URL_INVALID: "Utilisez un lien marchand HTTPS valide.",
  MY_DOHM_STATE_INVALID: "Le rattachement My DOHM doit être réinitialisé.",
  MY_DOHM_UNAVAILABLE: "My DOHM est momentanément indisponible. Votre compte CADOHM reste utilisable.",
  MY_DOHM_VERIFICATION_INVALID: "Ce code My DOHM est invalide ou expiré.",
  MY_DOHM_VERIFICATION_REQUIRED: "Demandez un nouveau code My DOHM.",
  NOTIFICATION_ACCESS_DENIED: "Cette notification ne vous appartient pas.",
  NOTIFICATION_TYPE_INVALID: "Ce type de notification n'est pas reconnu.",
  PASSWORD_INVALID: "Choisissez au moins 12 caractères avec une lettre et un chiffre.",
  RECOVERY_ATTEMPTS_EXCEEDED: "Trop d'essais ont été effectués. Demandez un nouveau code.",
  RECOVERY_EXPIRED: "Ce code de récupération a expiré. Demandez-en un nouveau.",
  RECOVERY_INVALID: "Ce code de récupération n'est pas valide.",
  SECRET_SANTA_DRAW_CONFLICT: "Les participants ont changé pendant le tirage. Recommencez après vérification.",
  SECRET_SANTA_EVENT_NOT_OPEN: "Ouvrez l'événement et confirmez les participants avant le tirage.",
  SECRET_SANTA_EXCLUSION_DENIED: "Seul l'organisateur peut exclure deux participants confirmés.",
  SECRET_SANTA_EXCLUSION_INVALID: "Choisissez deux participants distincts.",
  SECRET_SANTA_IMPOSSIBLE: "Aucun tirage ne respecte toutes les exclusions. Modifiez une contrainte avant de relancer.",
  SECRET_SANTA_ORGANIZER_REQUIRED: "Seul l'organisateur peut lancer le tirage.",
  VERIFICATION_EXPIRED: "Ce code a expiré.",
  VERIFICATION_INVALID: "Ce code n'est pas valide.",
  VERIFICATION_DELIVERY_UNAVAILABLE: "L'envoi du code n'est pas encore configuré sur cette cible.",
  WISH_DESCRIPTION_INVALID: "La description est trop longue.",
  WISH_PRICE_INVALID: "Saisissez un budget valide.",
  WISH_PRICE_RANGE_INVALID: "Le budget maximum doit être supérieur au minimum.",
  WISH_PRIORITY_INVALID: "Choisissez une priorité valide.",
  WISH_QUANTITY_INVALID: "La quantité doit être comprise entre 1 et 99.",
  WISH_TITLE_INVALID: "Le titre doit contenir entre 2 et 140 caractères.",
  WISH_VARIANTS_INVALID: "Les variantes sont trop longues.",
  WISH_ACCESS_DENIED: "Cette envie ne vous appartient pas."
};

export function App(): React.JSX.Element {
  const api = useMemo(() => new CadohmApiClient(), []);
  const [phase, setPhase] = useState<Phase>("auth");
  const [loginMode, setLoginMode] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [testCode, setTestCode] = useState<string | null>(null);
  const [verificationResent, setVerificationResent] = useState(false);
  const [myDohmCode, setMyDohmCode] = useState("");
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [giftLists, setGiftLists] = useState<readonly GiftListView[]>([]);
  const [events, setEvents] = useState<readonly EventView[]>([]);
  const [notifications, setNotifications] = useState<readonly NotificationView[]>([]);
  const [notificationPreferences, setNotificationPreferences] = useState<readonly NotificationPreferenceView[]>([]);
  const [dependentProfiles, setDependentProfiles] = useState<readonly DependentProfileView[]>([]);
  const [trustedPassportAccess, setTrustedPassportAccess] = useState<TrustedPassportAccessView>({ receivedPassports: [], viewers: [] });
  const [trustedPassportInvitationToken, setTrustedPassportInvitationToken] = useState("");
  const [testTrustedPassportInvitationToken, setTestTrustedPassportInvitationToken] = useState<string | null>(null);
  const [invitationToken, setInvitationToken] = useState("");
  const [testInvitationToken, setTestInvitationToken] = useState<string | null>(null);
  const [eventInvitationToken, setEventInvitationToken] = useState("");
  const [testEventInvitationToken, setTestEventInvitationToken] = useState<string | null>(null);
  const [dependentInvitationToken, setDependentInvitationToken] = useState("");
  const [testDependentInvitationToken, setTestDependentInvitationToken] = useState<string | null>(null);
  const [deletionPassword, setDeletionPassword] = useState("");
  const [deletionConfirmation, setDeletionConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.dashboard().then((value) => {
      setDashboard(value);
      setPhase(value.passport === null ? "onboarding" : "dashboard");
    }).catch(() => undefined);
  }, [api]);

  useEffect(() => {
    if (phase !== "dashboard") return;
    void Promise.all([
      api.giftLists(),
      api.events(),
      api.dependentProfiles(),
      api.trustedPassportAccess(),
      api.notifications(),
      api.notificationPreferences()
    ]).then(([
      lists,
      currentEvents,
      currentDependentProfiles,
      currentTrustedPassportAccess,
      currentNotifications,
      currentNotificationPreferences
    ]) => {
      setGiftLists(lists);
      setEvents(currentEvents);
      setDependentProfiles(currentDependentProfiles);
      setTrustedPassportAccess(currentTrustedPassportAccess);
      setNotifications(currentNotifications);
      setNotificationPreferences(currentNotificationPreferences);
    }).catch((caught: unknown) => setError(errorMessage(caught)));
  }, [api, phase]);

  async function submitAuth(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (loginMode) {
        await api.login({ email, password });
        const current = await api.dashboard();
        setDashboard(current);
        setPhase(current.passport === null ? "onboarding" : "dashboard");
      } else {
        const result = await api.register({ displayName: name, email, password });
        setTestCode(result.testOnlyVerificationCode ?? null);
        setVerificationResent(false);
        setPhase("verify");
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitVerification(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.verify({ code, email });
      setPhase("onboarding");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await api.resendVerification({ email });
      setTestCode(result.testOnlyVerificationCode ?? null);
      setCode("");
      setVerificationResent(true);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitRecoveryRequest(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.requestRecovery({ email });
      setTestCode(result.testOnlyRecoveryCode ?? null);
      setPhase("recovery-complete");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitRecoveryComplete(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.completeRecovery({ code, email, newPassword });
      const current = await api.dashboard();
      setDashboard(current);
      setPhase(current.passport === null ? "onboarding" : "dashboard");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitOnboarding(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const data = new FormData(event.currentTarget);
    try {
      const current = await api.completeOnboarding({
        preferences: {
          absoluteNoGos: formString(data, "absoluteNoGos"),
          favoriteColors: formString(data, "favoriteColors"),
          restrictions: formString(data, "restrictions"),
          sizes: formString(data, "sizes"),
          tastes: formString(data, "tastes")
        },
        visibility: formString(data, "visibility") as "private" | "trusted-people" | "event-participants",
        wish: {
          notes: formString(data, "notes"),
          priceMaxEuros: Number(formString(data, "priceMaxEuros")),
          priceMinEuros: Number(formString(data, "priceMinEuros")),
          title: formString(data, "title")
        }
      });
      setDashboard(current);
      setPhase("dashboard");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function logout(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.logout();
      setDashboard(null);
      setPassword("");
      setLoginMode(true);
      setPhase("auth");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function downloadExport(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const exported = await api.exportAccount();
      const url = URL.createObjectURL(new Blob([JSON.stringify(exported, null, 2)], { type: "application/json" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "cadohm-export.json";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function deleteAccount(): Promise<void> {
    if (deletionConfirmation !== "SUPPRIMER") {
      setError(messages.ACCOUNT_DELETE_CONFIRMATION_REQUIRED ?? "Confirmation requise.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.deleteAccount({ confirmation: "SUPPRIMER", password: deletionPassword });
      setDashboard(null);
      setPassword("");
      setDeletionPassword("");
      setDeletionConfirmation("");
      setLoginMode(false);
      setPhase("auth");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function createGiftList(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.createGiftList(formString(data, "title"));
      setGiftLists(await api.giftLists());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function inviteEditor(event: SyntheticEvent<HTMLFormElement, SubmitEvent>, listId: string): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    setTestInvitationToken(null);
    try {
      const result = await api.inviteGiftListEditor(listId, formString(data, "email"));
      setTestInvitationToken(result.testOnlyInvitationToken ?? null);
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function acceptInvitation(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.acceptGiftListInvitation(invitationToken);
      setGiftLists(await api.giftLists());
      setInvitationToken("");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function addGiftListItem(event: SyntheticEvent<HTMLFormElement, SubmitEvent>, listId: string): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.addGiftListItem(listId, formString(data, "title"));
      setGiftLists(await api.giftLists());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function revokeEditor(listId: string, editorAccountId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.revokeGiftListEditor(listId, editorAccountId);
      setGiftLists(await api.giftLists());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function createBirthdayEvent(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.createBirthdayEvent({
        beneficiaryName: formString(data, "beneficiaryName"),
        decisionDeadline: formString(data, "decisionDeadline"),
        eventDate: formString(data, "eventDate"),
        title: formString(data, "title")
      });
      setEvents(await api.events());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function createSecretSantaEvent(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.createSecretSantaEvent({
        avoidDirectReciprocity: data.get("avoidDirectReciprocity") === "on",
        decisionDeadline: formString(data, "decisionDeadline"),
        eventDate: formString(data, "eventDate"),
        title: formString(data, "title")
      });
      setEvents(await api.events());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function createCollectiveGiftEvent(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.createCollectiveGiftEvent({
        beneficiaryName: formString(data, "beneficiaryName"),
        decisionDeadline: formString(data, "decisionDeadline"),
        eventDate: formString(data, "eventDate"),
        title: formString(data, "title")
      });
      setEvents(await api.events());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function openEvent(eventId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.openEvent(eventId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function closeEvent(eventId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.closeEvent(eventId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function inviteEventParticipant(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    eventId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    setTestEventInvitationToken(null);
    try {
      const result = await api.inviteEventParticipant(eventId, formString(data, "email"));
      setTestEventInvitationToken(result.testOnlyInvitationToken ?? null);
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function inviteEventBeneficiary(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    eventId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    setTestEventInvitationToken(null);
    try {
      const result = await api.inviteEventBeneficiary(eventId, formString(data, "email"));
      setTestEventInvitationToken(result.testOnlyInvitationToken ?? null);
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function acceptEventInvitation(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.acceptEventInvitation(eventInvitationToken);
      setEvents(await api.events());
      setEventInvitationToken("");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function revokeEventParticipant(eventId: string, participantAccountId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.revokeEventParticipant(eventId, participantAccountId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function updateEventBeneficiaryOutcome(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    eventId: string
  ): Promise<void> {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.updateEventBeneficiaryOutcome(eventId, formString(data, "status"));
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function assignEventBuyer(eventId: string, buyerAccountId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.assignEventBuyer(eventId, buyerAccountId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function revokeEventBuyer(eventId: string, buyerAccountId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.revokeEventBuyer(eventId, buyerAccountId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function assignEventCoOrganizer(eventId: string, coOrganizerAccountId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.assignEventCoOrganizer(eventId, coOrganizerAccountId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function revokeEventCoOrganizer(eventId: string, coOrganizerAccountId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.revokeEventCoOrganizer(eventId, coOrganizerAccountId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function addEventGiftIdea(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    eventId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.addEventGiftIdea(eventId, formString(data, "title"));
      setEvents(await api.events());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function reserveEventGiftIdea(eventId: string, ideaId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.reserveEventGiftIdea(eventId, ideaId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function addEventGiftIdeaComment(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    eventId: string,
    ideaId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.addEventGiftIdeaComment(eventId, ideaId, formString(data, "body"));
      setEvents(await api.events());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function toggleEventGiftIdeaVote(eventId: string, ideaId: string, hasMyVote: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (hasMyVote) await api.removeEventGiftIdeaVote(eventId, ideaId);
      else await api.setEventGiftIdeaVote(eventId, ideaId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function decideEventGift(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    eventId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.decideEventGift(eventId, {
        explanation: formString(data, "explanation"),
        ideaId: formString(data, "ideaId")
      });
      setEvents(await api.events());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function cancelEventGiftReservation(eventId: string, ideaId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.cancelEventGiftReservation(eventId, ideaId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function setEventExternalPot(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    eventId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.setEventExternalPot(eventId, {
        provider: formString(data, "provider"),
        url: formString(data, "url")
      });
      setEvents(await api.events());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function addSecretSantaExclusion(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    eventId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.addSecretSantaExclusion(eventId, {
        accountAId: formString(data, "accountAId"),
        accountBId: formString(data, "accountBId"),
        reason: formString(data, "reason") as "manual" | "same-household" | "previous-year" | "age-group"
      });
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function drawSecretSanta(eventId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.drawSecretSanta(eventId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function updatePassportVisibility(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      setDashboard(await api.updatePassportVisibility(
        formString(data, "visibility") as PassportVisibility
      ));
      setEvents(await api.events());
      setTrustedPassportAccess(await api.trustedPassportAccess());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function sharePassportWithEvent(eventId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.sharePassportWithEvent(eventId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function updatePassportPreferences(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      setDashboard(await api.updatePassportPreferences({
        absoluteNoGos: formString(data, "absoluteNoGos"),
        favoriteColors: formString(data, "favoriteColors"),
        restrictions: formString(data, "restrictions"),
        sizes: formString(data, "sizes"),
        tastes: formString(data, "tastes")
      }));
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function revokePassportEventShare(eventId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.revokePassportEventShare(eventId);
      setEvents(await api.events());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function inviteTrustedPassportViewer(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    setTestTrustedPassportInvitationToken(null);
    try {
      const result = await api.inviteTrustedPassportViewer(formString(data, "email"));
      setTestTrustedPassportInvitationToken(result.testOnlyInvitationToken ?? null);
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function acceptTrustedPassportInvitation(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.acceptTrustedPassportInvitation(trustedPassportInvitationToken);
      setTrustedPassportAccess(await api.trustedPassportAccess());
      setTrustedPassportInvitationToken("");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function revokeTrustedPassportViewer(viewerAccountId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.revokeTrustedPassportViewer(viewerAccountId);
      setTrustedPassportAccess(await api.trustedPassportAccess());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function markNotificationRead(notificationId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.markNotificationRead(notificationId);
      setNotifications(await api.notifications());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function updateNotificationPreference(type: NotificationView["type"], enabled: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.updateNotificationPreference(type, enabled);
      setNotificationPreferences(await api.notificationPreferences());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function requestMyDohmVerification(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setDashboard(await api.requestMyDohmVerification());
      setMyDohmCode("");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function completeMyDohmVerification(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setDashboard(await api.completeMyDohmVerification(myDohmCode));
      setMyDohmCode("");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function addWish(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      setDashboard(await api.addWish({
        allowAlternative: data.get("allowAlternative") === "on",
        description: formString(data, "description"),
        priceMaxEuros: Number(formString(data, "priceMaxEuros")),
        priceMinEuros: Number(formString(data, "priceMinEuros")),
        priority: (formString(data, "priority") || null) as "low" | "medium" | "high" | null,
        quantity: Number(formString(data, "quantity")),
        title: formString(data, "title"),
        variants: formString(data, "variants")
      }));
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function addWishMerchantLink(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    wishId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.addWishMerchantLink(wishId, {
        isAffiliate: data.get("isAffiliate") === "on",
        merchantName: formString(data, "merchantName"),
        url: formString(data, "url")
      });
      setDashboard(await api.dashboard());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function createDependentProfile(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.createDependentProfile(formString(data, "displayName"));
      setDependentProfiles(await api.dependentProfiles());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function addDependentWish(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    profileId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    try {
      await api.addDependentWish(profileId, {
        priceMaxEuros: Number(formString(data, "priceMaxEuros")),
        priceMinEuros: Number(formString(data, "priceMinEuros")),
        title: formString(data, "title"),
        variants: formString(data, "variants")
      });
      setDependentProfiles(await api.dependentProfiles());
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function inviteDependentProfile(
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    profileId: string
  ): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setError(null);
    setTestDependentInvitationToken(null);
    try {
      const result = await api.inviteDependentProfile(
        profileId,
        formString(data, "email"),
        formString(data, "invitationType") as "co-guardian" | "autonomy-transfer"
      );
      setTestDependentInvitationToken(result.testOnlyInvitationToken ?? null);
      form.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function acceptDependentInvitation(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.acceptDependentInvitation(dependentInvitationToken);
      setDependentProfiles(await api.dependentProfiles());
      setDependentInvitationToken("");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function revokeDependentCoGuardian(profileId: string, accountId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.revokeDependentCoGuardian(profileId, accountId);
      setDependentProfiles(await api.dependentProfiles());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main>
      <header className="hero">
        <div className="product-mark" aria-label="CADOHM, actif temporaire de développement">CADOHM <small>DEV</small></div>
        <p className="eyebrow">Les bonnes idées, pour toutes les occasions.</p>
        <h1>Commencez par ce qui fera vraiment plaisir.</h1>
        <p className="intro">Un passeport cadeau privé, une première envie et des choix de partage explicites.</p>
      </header>

      <section className="card" aria-live="polite">
        <p className="step">{stepLabel(phase)}</p>
        {phase === "auth" && (
          <form onSubmit={(event) => { void submitAuth(event); }}>
            <h2>{loginMode ? "Retrouver mon compte" : "Créer mon compte CADOHM"}</h2>
            {!loginMode && <Field label="Nom d'usage"><input required minLength={2} maxLength={80} value={name} onChange={(event) => setName(event.target.value)} /></Field>}
            <Field label="Adresse e-mail"><input required type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></Field>
            <Field label="Mot de passe"><input required type="password" minLength={12} autoComplete={loginMode ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} /></Field>
            <button disabled={busy} type="submit">{busy ? "Patientez…" : loginMode ? "Me connecter" : "Créer mon compte"}</button>
            <button className="link" type="button" onClick={() => { setLoginMode(!loginMode); setError(null); }}>
              {loginMode ? "Créer un compte" : "J'ai déjà un compte"}
            </button>
            {loginMode && (
              <button className="link" type="button" onClick={() => { setPhase("recovery-request"); setError(null); }}>
                Mot de passe oublié
              </button>
            )}
          </form>
        )}

        {phase === "verify" && (
          <form onSubmit={(event) => { void submitVerification(event); }}>
            <h2>Vérifier mon contact</h2>
            <p>Un code à six chiffres a été préparé pour <strong>{email}</strong>.</p>
            {verificationResent && <p>Un nouveau code vient d’être envoyé. Seul le plus récent est valable.</p>}
            {testCode !== null && <p className="test-only">Mode TEST local — code : <strong>{testCode}</strong></p>}
            <Field label="Code à six chiffres"><input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value)} /></Field>
            <button disabled={busy} type="submit">Vérifier et continuer</button>
            <button className="link" disabled={busy} type="button" onClick={() => { void resendVerification(); }}>Renvoyer un code</button>
          </form>
        )}

        {phase === "recovery-request" && (
          <form onSubmit={(event) => { void submitRecoveryRequest(event); }}>
            <h2>Récupérer mon compte</h2>
            <p>Si cette adresse correspond à un compte actif, un code court sera envoyé.</p>
            <Field label="Adresse e-mail"><input required type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></Field>
            <button disabled={busy} type="submit">Demander un code</button>
            <button className="link" type="button" onClick={() => setPhase("auth")}>Retour à la connexion</button>
          </form>
        )}

        {phase === "recovery-complete" && (
          <form onSubmit={(event) => { void submitRecoveryComplete(event); }}>
            <h2>Choisir un nouveau mot de passe</h2>
            {testCode !== null && <p className="test-only">Mode TEST local — code : <strong>{testCode}</strong></p>}
            <Field label="Code à six chiffres"><input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value)} /></Field>
            <Field label="Nouveau mot de passe"><input required type="password" minLength={12} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></Field>
            <button disabled={busy} type="submit">Réinitialiser et me connecter</button>
          </form>
        )}

        {phase === "onboarding" && (
          <form onSubmit={(event) => { void submitOnboarding(event); }}>
            <h2>Créer mon passeport et ma première envie</h2>
            <Field label="Qui peut voir mon passeport ?">
              <select name="visibility" defaultValue="private">
                <option value="private">Moi uniquement</option>
                <option value="trusted-people">Mes proches autorisés</option>
                <option value="event-participants">Les participants d'un événement</option>
              </select>
            </Field>
            <Field label="Tailles utiles (facultatif)"><input name="sizes" maxLength={600} placeholder="Vêtements M, chaussures 39…" /></Field>
            <Field label="Couleurs préférées (facultatif)"><input name="favoriteColors" maxLength={600} /></Field>
            <Field label="Goûts et centres d'intérêt (facultatif)"><textarea name="tastes" maxLength={600} rows={2} /></Field>
            <Field label="Restrictions (facultatif)"><textarea name="restrictions" maxLength={600} rows={2} placeholder="Allergies, matières à éviter…" /></Field>
            <Field label="Refus absolus (facultatif)"><textarea name="absoluteNoGos" maxLength={600} rows={2} /></Field>
            <Field label="Mon envie"><input name="title" required minLength={2} maxLength={140} placeholder="Un cours de poterie, un livre…" /></Field>
            <div className="price-row">
              <Field label="Budget minimum (€)"><input name="priceMinEuros" required type="number" min="0" max="1000000" step="0.01" /></Field>
              <Field label="Budget maximum (€)"><input name="priceMaxEuros" required type="number" min="0" max="1000000" step="0.01" /></Field>
            </div>
            <Field label="Variantes ou précisions (facultatif)"><textarea name="notes" maxLength={600} rows={3} /></Field>
            <button disabled={busy} type="submit">Enregistrer mon passeport</button>
          </form>
        )}

        {phase === "dashboard" && dashboard !== null && (
          <div>
            <h2>Bonjour {dashboard.account.displayName}</h2>
            <p className="success">Votre passeport est enregistré et vos données ont été relues depuis le compte actif.</p>
            {notifications.some((notification) => notification.readAt === null) && (
              <section className="account-actions">
                <h3>Actions attendues</h3>
                {notifications.filter((notification) => notification.readAt === null).map((notification) => (
                  <div className="members" key={notification.id}>
                    <div>
                      <span>{notificationLabel(notification.type)}</span>
                      <button className="link" disabled={busy} type="button" onClick={() => { void markNotificationRead(notification.id); }}>Marquer comme lu</button>
                    </div>
                  </div>
                ))}
                <small>Les notifications restent neutres : aucun cadeau, montant, vote ou donneur n'apparaît hors de sa vue autorisée.</small>
              </section>
            )}
            <details className="account-actions">
              <summary>Préférences de notification</summary>
              {notificationPreferences.map((preference) => (
                <label className="check-field" key={preference.type}>
                  <input
                    checked={preference.inAppEnabled}
                    disabled={busy}
                    type="checkbox"
                    onChange={(event) => { void updateNotificationPreference(preference.type, event.target.checked); }}
                  />
                  {notificationPreferenceLabel(preference.type)}
                </label>
              ))}
              <small>Les canaux e-mail et push restent indisponibles tant que leurs fournisseurs ne sont pas raccordés.</small>
            </details>
            <dl className="summary">
              <div><dt>Visibilité</dt><dd>{visibilityLabel(dashboard.passport?.visibility)}</dd></div>
              <div><dt>Première envie</dt><dd>{dashboard.wishes[0]?.title ?? "À compléter"}</dd></div>
              <div><dt>My DOHM</dt><dd>{dashboard.myDohm.status === "linked" ? "Rattaché" : "Rattachement en attente, compte CADOHM utilisable"}</dd></div>
            </dl>
            {dashboard.myDohm.status !== "linked" && (
              <section className="account-actions">
                <h3>Rattacher mon profil My DOHM</h3>
                <p>Ce rattachement est facultatif : CADOHM, ses données et sa connexion restent autonomes.</p>
                {dashboard.myDohm.verificationRequired ? (
                  <form onSubmit={(event) => { void completeMyDohmVerification(event); }}>
                    <Field label="Code My DOHM à six chiffres">
                      <input
                        required
                        inputMode="numeric"
                        maxLength={6}
                        pattern="[0-9]{6}"
                        value={myDohmCode}
                        onChange={(event) => setMyDohmCode(event.target.value)}
                      />
                    </Field>
                    <button disabled={busy} type="submit">Vérifier et rattacher</button>
                  </form>
                ) : (
                  <button disabled={busy} type="button" onClick={() => { void requestMyDohmVerification(); }}>
                    Envoyer un code My DOHM
                  </button>
                )}
              </section>
            )}
            <section className="gift-lists">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Mon passeport cadeau</p>
                  <h3>Mes envies</h3>
                </div>
                <p>Décrivez l'idée indépendamment du vendeur et précisez les variantes qui comptent vraiment.</p>
              </div>
              <form className="wish-form" key={dashboard.passport?.visibility} onSubmit={(event) => { void updatePassportVisibility(event); }}>
                <Field label="Visibilité de mon passeport">
                  <select name="visibility" defaultValue={dashboard.passport?.visibility ?? "private"}>
                    <option value="private">Moi uniquement</option>
                    <option value="trusted-people">Mes proches autorisés</option>
                    <option value="event-participants">Participants des événements où je le partage</option>
                  </select>
                </Field>
                <small>Quitter le mode « participants » retire immédiatement tous les partages d'événements.</small>
                <button disabled={busy} type="submit">Mettre à jour la visibilité</button>
              </form>
              <form className="wish-form" onSubmit={(event) => { void updatePassportPreferences(event); }}>
                <h4>Mes repères durables</h4>
                <Field label="Tailles"><input name="sizes" maxLength={600} defaultValue={dashboard.passport?.sizes ?? ""} /></Field>
                <Field label="Couleurs préférées"><input name="favoriteColors" maxLength={600} defaultValue={dashboard.passport?.favoriteColors ?? ""} /></Field>
                <Field label="Goûts"><textarea name="tastes" maxLength={600} rows={2} defaultValue={dashboard.passport?.tastes ?? ""} /></Field>
                <Field label="Restrictions"><textarea name="restrictions" maxLength={600} rows={2} defaultValue={dashboard.passport?.restrictions ?? ""} /></Field>
                <Field label="Refus absolus"><textarea name="absoluteNoGos" maxLength={600} rows={2} defaultValue={dashboard.passport?.absoluteNoGos ?? ""} /></Field>
                <button disabled={busy} type="submit">Enregistrer mes préférences</button>
              </form>
              <div className="list-actions">
                {dashboard.passport?.visibility === "trusted-people" && (
                  <form onSubmit={(event) => { void inviteTrustedPassportViewer(event); }}>
                    <Field label="Autoriser un proche vérifié"><input name="email" required type="email" /></Field>
                    <button disabled={busy} type="submit">Envoyer l'invitation passeport</button>
                  </form>
                )}
                <form onSubmit={(event) => { void acceptTrustedPassportInvitation(event); }}>
                  <Field label="Invitation passeport reçue"><input required value={trustedPassportInvitationToken} onChange={(event) => setTrustedPassportInvitationToken(event.target.value)} /></Field>
                  <button disabled={busy} type="submit">Accéder au passeport</button>
                </form>
              </div>
              {testTrustedPassportInvitationToken !== null && (
                <p className="test-only">Mode TEST local — invitation passeport à transmettre : <strong>{testTrustedPassportInvitationToken}</strong></p>
              )}
              {trustedPassportAccess.viewers.length > 0 && (
                <div className="members">
                  <strong>Proches autorisés à voir mon passeport</strong>
                  {trustedPassportAccess.viewers.map((viewer) => (
                    <div key={viewer.accountId}>
                      <span>{viewer.displayName}</span>
                      <button className="link danger-link" disabled={busy} type="button" onClick={() => { void revokeTrustedPassportViewer(viewer.accountId); }}>Retirer l'accès</button>
                    </div>
                  ))}
                </div>
              )}
              {trustedPassportAccess.receivedPassports.length > 0 && (
                <div className="wish-grid">
                  {trustedPassportAccess.receivedPassports.map((shared) => (
                    <article className="wish-card" key={shared.ownerAccountId}>
                      <strong>Passeport de {shared.displayName}</strong>
                      {shared.passport.sizes.length > 0 && <p>Tailles : {shared.passport.sizes}</p>}
                      {shared.passport.favoriteColors.length > 0 && <p>Couleurs : {shared.passport.favoriteColors}</p>}
                      {shared.passport.tastes.length > 0 && <p>Goûts : {shared.passport.tastes}</p>}
                      {shared.passport.restrictions.length > 0 && <p>Restrictions : {shared.passport.restrictions}</p>}
                      {shared.passport.absoluteNoGos.length > 0 && <p>À ne pas offrir : {shared.passport.absoluteNoGos}</p>}
                      <ul className="gift-items">{shared.wishes.map((wish) => <li key={wish.id}>{wish.title}</li>)}</ul>
                    </article>
                  ))}
                </div>
              )}
              <div className="wish-grid">
                {dashboard.wishes.map((wish) => (
                  <article className="wish-card" key={wish.id}>
                    <div><strong>{wish.title}</strong>{wish.priority !== null && <span>{priorityLabel(wish.priority)}</span>}</div>
                    {wish.description.length > 0 && <p>{wish.description}</p>}
                    <p>{formatEuros(wish.priceMinCents)} à {formatEuros(wish.priceMaxCents)} · quantité {wish.quantity}</p>
                    {wish.variants.length > 0 && <p>Variantes : {wish.variants}</p>}
                    <small>{wish.allowAlternative ? "Alternative acceptée" : "Pas d'alternative"}</small>
                    {wish.merchantLinks.map((link) => (
                      <p className="merchant-link" key={link.id}>
                        <a href={link.url} target="_blank" rel="noreferrer">{link.merchantName}</a> · {link.isAffiliate ? "lien affilié — CADOHM peut recevoir une commission, sans modifier le classement" : "lien non affilié"}
                      </p>
                    ))}
                    <form className="compact-form" onSubmit={(event) => { void addWishMerchantLink(event, wish.id); }}>
                      <Field label="Marchand"><input name="merchantName" required minLength={2} maxLength={80} /></Field>
                      <Field label="Lien HTTPS"><input name="url" required type="url" pattern="https://.*" /></Field>
                      <label className="check-field"><input name="isAffiliate" type="checkbox" /> Ce lien est affilié</label>
                      <button disabled={busy} type="submit">Ajouter le lien avec sa divulgation</button>
                    </form>
                  </article>
                ))}
              </div>
              <form className="wish-form" onSubmit={(event) => { void addWish(event); }}>
                <Field label="Nouvelle envie"><input name="title" required minLength={2} maxLength={140} /></Field>
                <Field label="Description"><textarea name="description" maxLength={1200} rows={3} /></Field>
                <div className="price-row">
                  <Field label="Budget minimum (€)"><input name="priceMinEuros" required type="number" min="0" max="1000000" step="0.01" /></Field>
                  <Field label="Budget maximum (€)"><input name="priceMaxEuros" required type="number" min="0" max="1000000" step="0.01" /></Field>
                </div>
                <div className="price-row">
                  <Field label="Quantité"><input name="quantity" required type="number" min="1" max="99" defaultValue="1" /></Field>
                  <Field label="Priorité"><select name="priority" defaultValue=""><option value="">Sans priorité</option><option value="low">Faible</option><option value="medium">Moyenne</option><option value="high">Haute</option></select></Field>
                </div>
                <Field label="Variantes utiles"><input name="variants" maxLength={600} placeholder="Taille, couleur, modèle…" /></Field>
                <label className="check-field"><input name="allowAlternative" type="checkbox" defaultChecked /> J'accepte une alternative proche</label>
                <button disabled={busy} type="submit">Ajouter cette envie</button>
              </form>
            </section>
            <section className="gift-lists">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Accompagner sans confisquer</p>
                  <h3>Profils dépendants</h3>
                </div>
                <p>Un adulte peut gérer les envies, ajouter un coresponsable révocable ou proposer un transfert définitif vers le compte autonome de la personne.</p>
              </div>
              <div className="list-actions">
                <form onSubmit={(event) => { void createDependentProfile(event); }}>
                  <Field label="Nom du profil"><input name="displayName" required minLength={2} maxLength={80} /></Field>
                  <button disabled={busy} type="submit">Créer le profil géré</button>
                </form>
                <form onSubmit={(event) => { void acceptDependentInvitation(event); }}>
                  <Field label="Invitation reçue"><input required value={dependentInvitationToken} onChange={(event) => setDependentInvitationToken(event.target.value)} /></Field>
                  <button disabled={busy} type="submit">Accepter l'invitation</button>
                </form>
              </div>
              {testDependentInvitationToken !== null && (
                <p className="test-only">Mode TEST local — invitation profil à transmettre : <strong>{testDependentInvitationToken}</strong></p>
              )}
              {dependentProfiles.length === 0 && <p>Aucun profil dépendant accessible.</p>}
              <div className="list-grid">
                {dependentProfiles.map((profile) => (
                  <article className="gift-list" key={profile.id}>
                    <div className="gift-list-title">
                      <div><h4>{profile.displayName}</h4><p>{dependentRoleLabel(profile.accessRole)}</p></div>
                      <span>{profile.status === "managed" ? "Profil géré" : "Autonomie transférée"}</span>
                    </div>
                    <ul className="gift-items">
                      {profile.wishes.map((wish) => <li key={wish.id}>{wish.title}{wish.variants.length > 0 ? ` · ${wish.variants}` : ""}</li>)}
                      {profile.wishes.length === 0 && <li className="empty">Aucune envie enregistrée.</li>}
                    </ul>
                    <form className="compact-form" onSubmit={(event) => { void addDependentWish(event, profile.id); }}>
                      <Field label="Ajouter une envie"><input name="title" required minLength={2} maxLength={140} /></Field>
                      <div className="price-row">
                        <Field label="Minimum (€)"><input name="priceMinEuros" required type="number" min="0" step="0.01" /></Field>
                        <Field label="Maximum (€)"><input name="priceMaxEuros" required type="number" min="0" step="0.01" /></Field>
                      </div>
                      <Field label="Variantes"><input name="variants" maxLength={600} /></Field>
                      <button disabled={busy} type="submit">Ajouter</button>
                    </form>
                    {profile.guardians.length > 0 && (
                      <div className="members">
                        <strong>Responsables</strong>
                        {profile.guardians.map((guardian) => (
                          <div key={guardian.accountId}>
                            <span>{guardian.displayName} · {guardian.role === "primary-guardian" ? "principal" : "coresponsable"}</span>
                            {profile.accessRole === "primary-guardian" && guardian.role === "co-guardian" && (
                              <button className="link danger-link" disabled={busy} type="button" onClick={() => { void revokeDependentCoGuardian(profile.id, guardian.accountId); }}>Retirer</button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {profile.accessRole === "primary-guardian" && profile.status === "managed" && (
                      <form className="compact-form" onSubmit={(event) => { void inviteDependentProfile(event, profile.id); }}>
                        <Field label="Compte destinataire"><input name="email" required type="email" /></Field>
                        <Field label="Droit proposé"><select name="invitationType" defaultValue="co-guardian"><option value="co-guardian">Ajouter un coresponsable</option><option value="autonomy-transfer">Transférer définitivement l'autonomie</option></select></Field>
                        <button disabled={busy} type="submit">Préparer l'invitation</button>
                      </form>
                    )}
                    <small>Historique : {profile.audit.map((entry) => dependentAuditLabel(entry.action)).join(" · ")}</small>
                  </article>
                ))}
              </div>
            </section>
            <section className="gift-lists">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Choisir ensemble</p>
                  <h3>Listes cadeau partagées</h3>
                </div>
                <p>Chaque proche invité utilise son propre compte. Le propriétaire peut lui retirer l'accès à tout moment.</p>
              </div>
              <div className="list-actions">
                <form onSubmit={(event) => { void createGiftList(event); }}>
                  <Field label="Nouvelle liste"><input name="title" required minLength={2} maxLength={100} placeholder="Anniversaire de Léa" /></Field>
                  <button disabled={busy} type="submit">Créer la liste</button>
                </form>
                <form onSubmit={(event) => { void acceptInvitation(event); }}>
                  <Field label="Invitation reçue"><input required value={invitationToken} onChange={(event) => setInvitationToken(event.target.value)} placeholder="Coller le code d'invitation" /></Field>
                  <button disabled={busy} type="submit">Rejoindre la liste</button>
                </form>
              </div>
              {testInvitationToken !== null && (
                <p className="test-only">Mode TEST local — invitation à transmettre au compte invité : <strong>{testInvitationToken}</strong></p>
              )}
              {giftLists.length === 0 && <p>Aucune liste partagée pour le moment.</p>}
              <div className="list-grid">
                {giftLists.map((list) => (
                  <article className="gift-list" key={list.id}>
                    <div className="gift-list-title">
                      <h4>{list.title}</h4>
                      <span>{list.role === "owner" ? "Propriétaire" : "Proche invité"}</span>
                    </div>
                    <ul className="gift-items">
                      {list.items.map((item) => <li key={item.id}>{item.title}</li>)}
                      {list.items.length === 0 && <li className="empty">Aucune idée ajoutée.</li>}
                    </ul>
                    <form className="compact-form" onSubmit={(event) => { void addGiftListItem(event, list.id); }}>
                      <Field label="Ajouter une idée"><input name="title" required minLength={2} maxLength={140} /></Field>
                      <button disabled={busy} type="submit">Ajouter</button>
                    </form>
                    <div className="members">
                      <strong>Membres</strong>
                      {list.members.map((member) => (
                        <div key={member.accountId}>
                          <span>{member.displayName} · {member.role === "owner" ? "propriétaire" : "éditeur"}</span>
                          {list.role === "owner" && member.role === "editor" && (
                            <button className="link danger-link" disabled={busy} type="button" onClick={() => { void revokeEditor(list.id, member.accountId); }}>Retirer</button>
                          )}
                        </div>
                      ))}
                    </div>
                    {list.role === "owner" && (
                      <form className="compact-form" onSubmit={(event) => { void inviteEditor(event, list.id); }}>
                        <Field label="Inviter un proche par e-mail"><input name="email" required type="email" /></Field>
                        <button disabled={busy} type="submit">Envoyer l'invitation</button>
                      </form>
                    )}
                  </article>
                ))}
              </div>
            </section>
            <section className="gift-lists">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Préparer une occasion</p>
                  <h3>Événements et Secret Santa</h3>
                </div>
                <p>Le brouillon reste privé. Après ouverture, chaque participant rejoint avec son compte et une invitation qui lui est destinée.</p>
              </div>
              <div className="list-actions">
                <form onSubmit={(event) => { void createBirthdayEvent(event); }}>
                  <Field label="Titre"><input name="title" required minLength={2} maxLength={100} placeholder="Anniversaire de Léa" /></Field>
                  <Field label="Bénéficiaire"><input name="beneficiaryName" required minLength={2} maxLength={80} /></Field>
                  <Field label="Décider avant le"><input name="decisionDeadline" required type="date" min={new Date().toISOString().slice(0, 10)} /></Field>
                  <Field label="Date"><input name="eventDate" required type="date" min={new Date().toISOString().slice(0, 10)} /></Field>
                  <button disabled={busy} type="submit">Créer le brouillon</button>
                </form>
                <form onSubmit={(event) => { void acceptEventInvitation(event); }}>
                  <Field label="Invitation à un événement ou à son bilan"><input required value={eventInvitationToken} onChange={(event) => setEventInvitationToken(event.target.value)} placeholder="Coller le code d'invitation" /></Field>
                  <button disabled={busy} type="submit">Rejoindre l'événement</button>
                </form>
              </div>
              <form className="wish-form" onSubmit={(event) => { void createCollectiveGiftEvent(event); }}>
                <h4>Créer un cadeau collectif</h4>
                <Field label="Titre"><input name="title" required minLength={2} maxLength={100} placeholder="Cadeau de départ du groupe" /></Field>
                <Field label="Bénéficiaire"><input name="beneficiaryName" required minLength={2} maxLength={80} /></Field>
                <Field label="Décider avant le"><input name="decisionDeadline" required type="date" min={new Date().toISOString().slice(0, 10)} /></Field>
                <Field label="Date"><input name="eventDate" required type="date" min={new Date().toISOString().slice(0, 10)} /></Field>
                <button disabled={busy} type="submit">Créer le brouillon collectif</button>
              </form>
              <form className="wish-form" onSubmit={(event) => { void createSecretSantaEvent(event); }}>
                <h4>Créer un Secret Santa</h4>
                <Field label="Titre"><input name="title" required minLength={2} maxLength={100} placeholder="Secret Santa du groupe" /></Field>
                <Field label="Décider avant le"><input name="decisionDeadline" required type="date" min={new Date().toISOString().slice(0, 10)} /></Field>
                <Field label="Date"><input name="eventDate" required type="date" min={new Date().toISOString().slice(0, 10)} /></Field>
                <label className="check-field"><input name="avoidDirectReciprocity" type="checkbox" defaultChecked /> Interdire que deux personnes se tirent mutuellement</label>
                <button disabled={busy} type="submit">Créer le brouillon Secret Santa</button>
              </form>
              {testEventInvitationToken !== null && (
                <p className="test-only">Mode TEST local — invitation événement à transmettre : <strong>{testEventInvitationToken}</strong></p>
              )}
              {events.length === 0 && <p>Aucun événement à organiser ou à rejoindre.</p>}
              <div className="list-grid">
                {events.map((currentEvent) => (
                  <article className="gift-list" key={currentEvent.id}>
                    <div className="gift-list-title">
                      <div>
                        <h4>{currentEvent.title}</h4>
                        <p>{eventKindLabel(currentEvent.kind)} · {currentEvent.beneficiaryName} · décision avant le {currentEvent.decisionDeadline} · événement le {currentEvent.eventDate}</p>
                      </div>
                      <span>{eventStatusLabel(currentEvent.status)}</span>
                    </div>
                    {currentEvent.role === "organizer" && currentEvent.status === "draft" && (
                      <button className="secondary" disabled={busy} type="button" onClick={() => { void openEvent(currentEvent.id); }}>Ouvrir aux invitations</button>
                    )}
                    {currentEvent.role === "organizer" && currentEvent.status === "open" && (
                      <button className="secondary" disabled={busy} type="button" onClick={() => { void closeEvent(currentEvent.id); }}>Clôturer l'événement</button>
                    )}
                    {currentEvent.status === "closed" && currentEvent.beneficiaryOutcome !== null && (
                      <div className="secret-zone">
                        <strong>Bilan révélé au bénéficiaire</strong>
                        <p>Cadeau : <strong>{currentEvent.beneficiaryOutcome.giftTitle}</strong></p>
                        {currentEvent.role === "beneficiary" ? (
                          <form className="compact-form" onSubmit={(event) => { void updateEventBeneficiaryOutcome(event, currentEvent.id); }}>
                            <Field label="Après l'événement"><select name="status" defaultValue={currentEvent.beneficiaryOutcome.status === "pending" ? "received" : currentEvent.beneficiaryOutcome.status}><option value="received">Reçu</option><option value="not-received">Non reçu</option><option value="exchange">À échanger</option><option value="keep-in-wishes">Conserver dans mes envies</option></select></Field>
                            <button disabled={busy} type="submit">Enregistrer mon choix</button>
                          </form>
                        ) : <p>Retour du bénéficiaire : {beneficiaryOutcomeLabel(currentEvent.beneficiaryOutcome.status)}</p>}
                        <small>Les idées écartées, commentaires, votes, réservations et décisions du groupe restent privés.</small>
                      </div>
                    )}
                    {currentEvent.role === "organizer" && currentEvent.status === "closed" && currentEvent.kind !== "secret-santa" && currentEvent.beneficiaryOutcome === null && (
                      <form className="compact-form" onSubmit={(event) => { void inviteEventBeneficiary(event, currentEvent.id); }}>
                        <Field label="Inviter le bénéficiaire au bilan"><input name="email" required type="email" /></Field>
                        <button disabled={busy} type="submit">Envoyer l'invitation au bilan</button>
                      </form>
                    )}
                    {currentEvent.status === "open" && (
                      <div className="secret-zone">
                        <strong>Passeports partagés avec les participants</strong>
                        {currentEvent.sharedPassports.map((shared) => (
                          <div key={shared.ownerAccountId}>
                            <p><strong>{shared.displayName}</strong></p>
                            {shared.passport.sizes.length > 0 && <p>Tailles : {shared.passport.sizes}</p>}
                            {shared.passport.favoriteColors.length > 0 && <p>Couleurs : {shared.passport.favoriteColors}</p>}
                            {shared.passport.tastes.length > 0 && <p>Goûts : {shared.passport.tastes}</p>}
                            {shared.passport.restrictions.length > 0 && <p>Restrictions : {shared.passport.restrictions}</p>}
                            {shared.passport.absoluteNoGos.length > 0 && <p>À ne pas offrir : {shared.passport.absoluteNoGos}</p>}
                            <ul className="gift-items">
                              {shared.wishes.map((wish) => <li key={wish.id}>{wish.title} · {formatEuros(wish.priceMinCents)} à {formatEuros(wish.priceMaxCents)}</li>)}
                              {shared.wishes.length === 0 && <li className="empty">Aucune envie publiée.</li>}
                            </ul>
                          </div>
                        ))}
                        {currentEvent.sharedPassports.length === 0 && <p>Aucun participant n'a partagé son passeport.</p>}
                        {currentEvent.sharedPassports.some((shared) => shared.ownerAccountId === dashboard.account.id)
                          ? <button className="secondary" disabled={busy} type="button" onClick={() => { void revokePassportEventShare(currentEvent.id); }}>Retirer mon passeport de cet événement</button>
                          : dashboard.passport?.visibility === "event-participants" && <button className="secondary" disabled={busy} type="button" onClick={() => { void sharePassportWithEvent(currentEvent.id); }}>Partager mon passeport avec cet événement</button>}
                        {dashboard.passport?.visibility !== "event-participants" && <small>Passez votre passeport en visibilité « participants » pour choisir cet événement.</small>}
                      </div>
                    )}
                    {currentEvent.kind === "secret-santa" && currentEvent.secretSanta !== null && (
                      <div className="secret-zone">
                        <strong>Mon tirage Secret Santa</strong>
                        {currentEvent.secretSanta.myRecipient === null
                          ? <p>Aucun résultat actif. Seul l'organisateur peut valider un tirage faisable.</p>
                          : <p>J'offre un cadeau à <strong>{currentEvent.secretSanta.myRecipient.displayName}</strong>. Ce résultat n'est visible que dans ma session.</p>}
                        <small>Version active : {currentEvent.secretSanta.activeDrawVersion ?? "aucune"} · exclusions : {currentEvent.secretSanta.exclusionCount} · réciprocité directe : {currentEvent.secretSanta.avoidDirectReciprocity ? "interdite" : "autorisée"}</small>
                        {currentEvent.role === "organizer" && currentEvent.status === "open" && currentEvent.members.length >= 2 && (
                          <form className="compact-form" onSubmit={(event) => { void addSecretSantaExclusion(event, currentEvent.id); }}>
                            <Field label="Première personne"><select name="accountAId">{currentEvent.members.map((member) => <option key={member.accountId} value={member.accountId}>{member.displayName}</option>)}</select></Field>
                            <Field label="Seconde personne"><select name="accountBId" defaultValue={currentEvent.members[1]?.accountId}>{currentEvent.members.map((member) => <option key={member.accountId} value={member.accountId}>{member.displayName}</option>)}</select></Field>
                            <Field label="Motif bloquant"><select name="reason"><option value="manual">Exclusion manuelle</option><option value="same-household">Même foyer / couple</option><option value="previous-year">Association d'une année précédente</option><option value="age-group">Contrainte d'âge ou de groupe</option></select></Field>
                            <button disabled={busy} type="submit">Ajouter l'exclusion</button>
                          </form>
                        )}
                        {currentEvent.role === "organizer" && currentEvent.status === "open" && (
                          <button className="secondary" disabled={busy} type="button" onClick={() => { void drawSecretSanta(currentEvent.id); }}>Vérifier et lancer un nouveau tirage</button>
                        )}
                      </div>
                    )}
                    {currentEvent.status === "open" && (
                      <div className="secret-zone">
                        <strong>Idées secrètes · invisibles au bénéficiaire</strong>
                        <ul className="gift-items">
                          {currentEvent.giftIdeas.map((idea) => (
                            <li key={idea.id}>
                              <div>
                                <span>{idea.title}</span>
                                <button className="link" disabled={busy} type="button" onClick={() => { void toggleEventGiftIdeaVote(currentEvent.id, idea.id, idea.hasMyVote); }}>{idea.hasMyVote ? "Retirer mon vote" : "Voter"}</button>
                                {idea.reservedByAccountId === null && (
                                  <button className="link" disabled={busy} type="button" onClick={() => { void reserveEventGiftIdea(currentEvent.id, idea.id); }}>Réserver</button>
                                )}
                                {idea.reservedByAccountId === dashboard.account.id && (
                                  <button className="link danger-link" disabled={busy} type="button" onClick={() => { void cancelEventGiftReservation(currentEvent.id, idea.id); }}>Libérer ma réservation</button>
                                )}
                              </div>
                              <small>{idea.voteCount} vote{idea.voteCount > 1 ? "s" : ""} indicatif{idea.voteCount > 1 ? "s" : ""} · la personne chargée de l'achat garde la décision finale.</small>
                              {idea.reservedByAccountId !== null && idea.reservedByAccountId !== dashboard.account.id && (
                                <small>Réservé par {idea.reservedByDisplayName}</small>
                              )}
                              <ul className="gift-items">
                                {idea.comments.map((comment) => <li key={comment.id}><strong>{comment.authorDisplayName}</strong> · {comment.body}</li>)}
                              </ul>
                              <form className="compact-form" onSubmit={(event) => { void addEventGiftIdeaComment(event, currentEvent.id, idea.id); }}>
                                <Field label="Commentaire privé"><input name="body" required minLength={2} maxLength={600} /></Field>
                                <button disabled={busy} type="submit">Commenter</button>
                              </form>
                            </li>
                          ))}
                          {currentEvent.giftIdeas.length === 0 && <li className="empty">Aucune idée secrète.</li>}
                        </ul>
                        <form className="compact-form" onSubmit={(event) => { void addEventGiftIdea(event, currentEvent.id); }}>
                          <Field label="Proposer une idée secrète"><input name="title" required minLength={2} maxLength={140} /></Field>
                          <button disabled={busy} type="submit">Proposer</button>
                        </form>
                        {currentEvent.decisionHistory.length > 0 && (
                          <div className="members">
                            <strong>Historique des décisions d'achat</strong>
                            {currentEvent.decisionHistory.map((decision) => (
                              <div key={decision.id}>
                                <span>v{decision.version} · {decision.ideaTitle} · {decision.explanation} · {decision.decidedByDisplayName}</span>
                                <small>{decision.status === "active" ? "Décision actuelle" : "Décision remplacée"}</small>
                              </div>
                            ))}
                          </div>
                        )}
                        {(currentEvent.role === "organizer" || currentEvent.members.some(
                          (member) => member.accountId === dashboard.account.id && member.isBuyer
                        )) && currentEvent.giftIdeas.length > 0 && (
                          <form className="compact-form" onSubmit={(event) => { void decideEventGift(event, currentEvent.id); }}>
                            <Field label="Décision finale d'achat"><select name="ideaId">{currentEvent.giftIdeas.map((idea) => <option key={idea.id} value={idea.id}>{idea.title} · {idea.voteCount} vote{idea.voteCount > 1 ? "s" : ""}</option>)}</select></Field>
                            <Field label="Explication au groupe"><input name="explanation" required minLength={2} maxLength={600} /></Field>
                            <button disabled={busy} type="submit">Enregistrer la décision</button>
                          </form>
                        )}
                      </div>
                    )}
                    {currentEvent.externalPotUrl !== null && (
                      <p className="external-link">
                        Cagnotte gérée hors CADOHM par {currentEvent.externalPotProvider} · <a href={currentEvent.externalPotUrl} target="_blank" rel="noreferrer">Ouvrir le service externe</a>. Aucun fonds ne transite par CADOHM.
                      </p>
                    )}
                    {currentEvent.role === "organizer" && currentEvent.status === "open" && (
                      <form className="compact-form" onSubmit={(event) => { void setEventExternalPot(event, currentEvent.id); }}>
                        <Field label="Service de cagnotte externe"><input name="provider" required minLength={2} maxLength={80} /></Field>
                        <Field label="Lien HTTPS"><input name="url" required type="url" pattern="https://.*" /></Field>
                        <button disabled={busy} type="submit">Enregistrer le lien externe</button>
                      </form>
                    )}
                    {currentEvent.role !== "beneficiary" && <div className="members">
                      <strong>Membres</strong>
                      {currentEvent.members.map((member) => (
                        <div key={member.accountId}>
                          <span>{member.displayName} · {member.role === "organizer" ? "organisateur" : "participant"}{member.isCoOrganizer ? " · coorganisateur" : ""}{member.isBuyer ? " · acheteur désigné" : ""}</span>
                          {currentEvent.status === "open" && currentEvent.role === "organizer" && member.role === "participant" && (
                            <>
                              {member.isBuyer
                                ? <button className="link danger-link" disabled={busy} type="button" onClick={() => { void revokeEventBuyer(currentEvent.id, member.accountId); }}>Retirer le rôle d'acheteur</button>
                                : <button className="link" disabled={busy} type="button" onClick={() => { void assignEventBuyer(currentEvent.id, member.accountId); }}>Désigner comme acheteur</button>}
                              {member.isCoOrganizer
                                ? <button className="link danger-link" disabled={busy} type="button" onClick={() => { void revokeEventCoOrganizer(currentEvent.id, member.accountId); }}>Retirer le rôle de coorganisateur</button>
                                : <button className="link" disabled={busy} type="button" onClick={() => { void assignEventCoOrganizer(currentEvent.id, member.accountId); }}>Désigner comme coorganisateur</button>}
                            </>
                          )}
                          {currentEvent.status === "open" && (currentEvent.role === "organizer" || currentEvent.members.some(
                            (candidate) => candidate.accountId === dashboard.account.id && candidate.isCoOrganizer
                          )) && member.role === "participant" && member.accountId !== dashboard.account.id && (
                            <button className="link danger-link" disabled={busy} type="button" onClick={() => { void revokeEventParticipant(currentEvent.id, member.accountId); }}>Retirer de l'événement</button>
                          )}
                        </div>
                      ))}
                    </div>}
                    {(currentEvent.role === "organizer" || currentEvent.members.some(
                      (member) => member.accountId === dashboard.account.id && member.isCoOrganizer
                    )) && currentEvent.status === "open" && (
                      <form className="compact-form" onSubmit={(event) => { void inviteEventParticipant(event, currentEvent.id); }}>
                        <Field label="Inviter un participant par e-mail"><input name="email" required type="email" /></Field>
                        <button disabled={busy} type="submit">Envoyer l'invitation</button>
                      </form>
                    )}
                  </article>
                ))}
              </div>
            </section>
            <button className="secondary" disabled={busy} type="button" onClick={() => { void logout(); }}>
              Me déconnecter
            </button>
            <section className="account-actions">
              <h3>Mes données</h3>
              <button className="secondary" disabled={busy} type="button" onClick={() => { void downloadExport(); }}>
                Télécharger mon export
              </button>
              <details>
                <summary>Supprimer définitivement mon compte</summary>
                <p>Les données CADOHM seront supprimées. Le profil My DOHM et les autres applications restent indépendants.</p>
                <Field label="Mot de passe actuel"><input type="password" autoComplete="current-password" value={deletionPassword} onChange={(event) => setDeletionPassword(event.target.value)} /></Field>
                <Field label="Recopiez SUPPRIMER"><input value={deletionConfirmation} onChange={(event) => setDeletionConfirmation(event.target.value)} /></Field>
                <button className="danger" disabled={busy || deletionConfirmation !== "SUPPRIMER" || deletionPassword.length === 0} type="button" onClick={() => { void deleteAccount(); }}>
                  Supprimer mon compte
                </button>
              </details>
            </section>
          </div>
        )}

        {error !== null && <p className="error" role="alert">{error}</p>}
      </section>
      <footer>Actifs visuels temporaires · aucune identité finale intégrée</footer>
    </main>
  );
}

function Field(props: { readonly children: React.ReactNode; readonly label: string }): React.JSX.Element {
  return <label className="field"><span>{props.label}</span>{props.children}</label>;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiClientError) return messages[error.code] ?? `Une erreur est survenue (${error.code}).`;
  return "Le service local n'est pas disponible.";
}

function stepLabel(phase: Phase): string {
  if (phase === "auth") return "Étape 1 sur 3 · accès autonome";
  if (phase === "verify") return "Étape 2 sur 3 · contact vérifié";
  if (phase === "recovery-request" || phase === "recovery-complete") return "Récupération autonome";
  if (phase === "onboarding") return "Étape 3 sur 3 · première valeur";
  return "Tableau de bord";
}

function visibilityLabel(value: string | undefined): string {
  if (value === "trusted-people") return "Proches autorisés";
  if (value === "event-participants") return "Participants d'un événement";
  return "Privé";
}

function eventKindLabel(value: EventView["kind"]): string {
  if (value === "collective-gift") return "Cadeau collectif";
  if (value === "secret-santa") return "Secret Santa";
  return "Anniversaire";
}

function eventStatusLabel(value: EventView["status"]): string {
  if (value === "draft") return "Brouillon privé";
  if (value === "closed") return "Clôturé";
  return "Ouvert";
}

function beneficiaryOutcomeLabel(
  value: NonNullable<EventView["beneficiaryOutcome"]>["status"]
): string {
  if (value === "received") return "reçu";
  if (value === "not-received") return "non reçu";
  if (value === "exchange") return "à échanger";
  if (value === "keep-in-wishes") return "à conserver dans ses envies";
  return "en attente";
}

function notificationLabel(type: NotificationView["type"]): string {
  if (type === "event-invitation") return "Une invitation à un événement vous attend dans CADOHM.";
  if (type === "passport-invitation") return "Une invitation à consulter un passeport vous attend dans CADOHM.";
  if (type === "event-decision-due") return "Une décision de cadeau arrive à échéance dans un événement que vous organisez.";
  return "Un résultat Secret Santa est disponible dans votre événement autorisé.";
}

function notificationPreferenceLabel(type: NotificationView["type"]): string {
  if (type === "event-invitation") return "Afficher les invitations à un événement";
  if (type === "passport-invitation") return "Afficher les invitations à consulter un passeport";
  if (type === "event-decision-due") return "Afficher les échéances de décision d'un événement";
  return "Afficher les nouveaux résultats Secret Santa";
}

function formString(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

function priorityLabel(value: "low" | "medium" | "high"): string {
  if (value === "high") return "Priorité haute";
  if (value === "medium") return "Priorité moyenne";
  return "Priorité faible";
}

function formatEuros(cents: number): string {
  return new Intl.NumberFormat("fr-FR", { currency: "EUR", style: "currency" }).format(cents / 100);
}

function dependentRoleLabel(value: DependentProfileView["accessRole"]): string {
  if (value === "primary-guardian") return "Responsable principal";
  if (value === "co-guardian") return "Coresponsable";
  return "Profil autonome";
}

function dependentAuditLabel(action: DependentProfileView["audit"][number]["action"]): string {
  if (action === "created") return "créé";
  if (action === "wish-added") return "envie ajoutée";
  if (action === "co-guardian-added") return "coresponsable ajouté";
  if (action === "co-guardian-revoked") return "coresponsable retiré";
  return "autonomie transférée";
}
